export interface AdfParserConfig {
	/**
	 * When enabled, attempts to correct unknown node types by finding the closest
	 * valid type in the schema using Levenshtein distance (e.g. "paragrap" → "paragraph").
	 * Only applies when the node type is unrecognized and a parent context is available.
	 * @default false
	 */
	fuzzyNodeTypeMatching?: boolean;
}

// ADF Node Types
// Represents any ADF node that can be at the root level
export interface AdfNode {
	[key: string]: unknown; // Allow additional properties
	attrs?: Record<string, unknown>;
	content?: Array<ContentNode>;
	marks?: Mark[];
	text?: string;
	type: string;
	version?: number;
}

export interface ContentNode {
	[key: string]: unknown; // Allow additional properties
	attrs?: Record<string, unknown>;
	content?: Array<ContentNode>;
	marks?: Mark[];
	text?: string;
	type: string;
}

export interface Mark {
	attrs?: Record<string, unknown>;
	type: string;
}

export interface Repair {
	context: string;
	expansion?: number;
	from: string;
	// For unwrap repairs: true if the unwrapped node is preserved (moved up), false if removed
	nodePreserved?: boolean;
	to: string;
	type: 'skip' | 'skip_empty_text' | 'infer' | 'duplicate_key' | 'wrap' | 'unwrap' | 'lift';
}

// Symbol to mark nodes that should be lifted to parent level
export const LIFT_MARKER: unique symbol = Symbol('lift');

export interface LiftedNode {
	/** If true, close the current container and lift remaining siblings too */
	closeContainer?: boolean;
	[LIFT_MARKER]: true;
	/** Single node or multiple nodes to lift */
	node: ContentNode | ContentNode[];
}

export function isLiftedNode(value: unknown): value is LiftedNode {
	return typeof value === 'object' && value !== null && LIFT_MARKER in value;
}

// Tokenizer Types
export type TokenType =
	| 'STRING'
	| 'NUMBER'
	| 'BOOLEAN'
	| 'NULL'
	| 'LEFT_BRACE'
	| 'RIGHT_BRACE'
	| 'LEFT_BRACKET'
	| 'RIGHT_BRACKET'
	| 'COLON'
	| 'COMMA'
	| 'EOF';

type BaseToken<T extends TokenType, V extends string = string> = {
	position: number;
	type: T;
	value: V;
};

export type Token =
	| BaseToken<'STRING', string>
	| BaseToken<'NUMBER', string>
	| BaseToken<'BOOLEAN', 'true' | 'false'>
	| BaseToken<'NULL', 'null'>
	| BaseToken<'LEFT_BRACE', '{'>
	| BaseToken<'RIGHT_BRACE', '}'>
	| BaseToken<'LEFT_BRACKET', '['>
	| BaseToken<'RIGHT_BRACKET', ']'>
	| BaseToken<'COLON', ':'>
	| BaseToken<'COMMA', ','>
	| BaseToken<'EOF', ''>;

export type TokenOfType<T extends TokenType> = Extract<Token, { type: T }>;

export type PrimitiveValue = string | number | boolean | null | undefined;

// Parser-specific types
export type NodeHandle = string;

export interface StreamingCallbacks {
	/**
	 * Called when the node is fully parsed with all its data
	 */
	onNodeComplete?: (handle: NodeHandle, node: ContentNode) => void;

	/**
	 * Called immediately when a node's "type" is discovered
	 * Return a handle to track this node for future updates
	 */
	onNodeStart?: (nodeType: string, parentType: string | undefined) => NodeHandle;

	/**
	 * Called as properties are discovered during parsing (text, attrs, marks, etc)
	 */
	onNodeUpdate?: (handle: NodeHandle, updates: Partial<ContentNode>) => void;

	/**
	 * Called when repairs are applied
	 */
	onRepair?: (repair: Repair) => void;
}
